import { useMemo } from 'react';
import _ from 'lodash';
import cn from 'classnames';

import { Typography, Markdown } from '@scale/scaleui';
import { SuggestionsProvider } from 'react-scale/components/scalers/tasks/textcollection/suggestions';
import { DocumentProvider } from 'react-scale/components/scalers/tasks/textcollection/document';
import FieldSection from 'react-scale/components/scalers/tasks/textcollection/FieldSection';
import {
  CategoryField,
  NumberField,
  TextCollectionAttachmentType,
  TextCollectionField,
  TextCollectionParams,
  TextField,
} from '@scale/shared/types/params/textcollection';
import {
  CategoryFieldAnnotation,
  NumberFieldAnnotation,
  TextCollectionAnnotation,
  TextCollectionResponse,
  TextFieldAnnotation,
} from '@scale/shared/types/response/textcollection';

const NOOP_FUNCTION = (..._params: any[]) => {};

const emptySectionProps = {
  setAnnotation: NOOP_FUNCTION,
  setAnnotationIndex: NOOP_FUNCTION,
  deleteAnnotationIndex: NOOP_FUNCTION,
  pushAnnotations: NOOP_FUNCTION,
  addBreadcrumb: NOOP_FUNCTION,
  autoComplete: () => [],
  quickAddResponses: [],
  unitResponsesByField: {},
  bboxFieldToEdit: null,
  setBboxFieldToEdit: NOOP_FUNCTION,
  attachment: { type: TextCollectionAttachmentType.Unprocessed, content: '' },
  setSingleAnnotation: NOOP_FUNCTION,
};

function toScalar(element: any) {
  while (_.isArray(element) && element.length > 0) {
    element = element[0];
  }
  return element;
}

function NumberToLabelField({
  field,
  annotation,
}: {
  field: NumberField;
  annotation: NumberFieldAnnotation;
}) {
  const labels = useMemo(() => {
    const min = field.min || 0;
    const max = field.max || 5;
    if (field.labels_list_override) {
      return field.labels_list_override.split(',').map(label => label.trim());
    } else if (field.prefix && field.suffix && field.mid_label) {
      const mid = (min + max) / 2;
      const between = mid - min - 1;
      return [
        field.prefix,
        ..._.range(between).map(() => `Between "${field.prefix}" and "${field.mid_label}"`),
        field.mid_label,
        ..._.range(between).map(() => `Between "${field.mid_label}" and "${field.suffix}"`),
        field.suffix,
      ];
    } else {
      return _.range(min, max + 1).map(i => i.toString());
    }
  }, [field]);

  const responseValue = toScalar(annotation.response);
  const responseIndex = !_.isNil(responseValue) ? parseInt(responseValue) : 1;
  const responseText = !_.isNil(responseIndex) ? labels[responseIndex - 1] : 'n/a';

  const color = useMemo(() => {
    const mid = (labels.length + 1) / 2;
    if (responseIndex < mid) {
      return 'text-danger-700';
    } else if (responseIndex < labels.length) {
      return 'text-primary-900';
    } else {
      return 'text-success-900';
    }
  }, [responseIndex, labels]);

  return (
    <TitleValuePair
      title={field.title}
      value={responseText}
      color={color}
      suffix={`(${responseIndex}/${labels.length})`}
    />
  );
}

function CategoryFieldCompact({
  field,
  annotation,
}: {
  field: CategoryField;
  annotation: CategoryFieldAnnotation;
}) {
  const responseText = useMemo(() => {
    const category = toScalar(annotation.response);
    const matching = field.choices.filter(c => c.value === category);
    if (matching.length > 0) {
      return matching[0].label;
    } else {
      return 'n/a';
    }
  }, [field, annotation]);

  return <TitleValuePair title={field.title} value={responseText} />;
}

function TextFieldCompact({
  field,
  annotation,
}: {
  field: TextField;
  annotation: TextFieldAnnotation;
}) {
  return (
    <div className="p-2">
      <Typography.Subtitle3>{field.title}</Typography.Subtitle3>
      <div className="my-2 border border-neutral-200 rounded-md p-4">
        <Markdown markdown={toScalar(annotation.response)} />
      </div>
    </div>
  );
}

function TitleValuePair({
  title,
  value,
  color,
  suffix = '',
}: {
  title: string;
  value: any;
  color?: string;
  suffix?: string;
}) {
  return (
    <div
      className={cn('text-md px-2 flex items-center justify-between', {
        'text-neutral-400': !value,
      })}
    >
      <div className="font-medium">{title}</div>
      <div className="flex items-center gap-1">
        <span className={cn('font-medium', color)}>{value || 'n/a'}</span>
        <span className="text-xs">{suffix}</span>
      </div>
    </div>
  );
}

function CompactFieldSection({
  field,
  annotation,
  currentAnnotations,
}: {
  field: TextCollectionField;
  annotation: TextCollectionAnnotation;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
}) {
  if (field.type === 'number' && field.labels_list_override) {
    return <NumberToLabelField field={field} annotation={annotation as NumberFieldAnnotation} />;
  } else if (field.type === 'text') {
    return <TextFieldCompact field={field} annotation={annotation as TextFieldAnnotation} />;
  } else if (field.type === 'category') {
    return (
      <CategoryFieldCompact field={field} annotation={annotation as CategoryFieldAnnotation} />
    );
  }
  return (
    <FieldSection
      field={{ ...field, description: '' }}
      annotation={annotation}
      currentAnnotations={currentAnnotations}
      {...emptySectionProps}
    />
  );
}

export function TextCollectionResponseCompact({
  params,
  response,
}: {
  params: TextCollectionParams;
  response: TextCollectionResponse;
}) {
  const fields = params.fields;
  const annotations = response?.annotations || {};
  return (
    <SuggestionsProvider suggestionSources={params.suggestionSources}>
      <DocumentProvider attachments={0} fields={fields}>
        <div className="flex flex-col gap-2 p-4 border border-neutral-200 rounded-md">
          {fields.map((field: TextCollectionField) => {
            const annotation = annotations[field.field_id];
            if (!annotation) {
              return <TitleValuePair title={field.title} value="" />;
            }
            return (
              <CompactFieldSection
                key={field.field_id}
                field={field}
                annotation={annotation}
                currentAnnotations={annotations}
              />
            );
          })}
        </div>
      </DocumentProvider>
    </SuggestionsProvider>
  );
}
